import React, { memo } from 'react';
import renderValue from '../../utility/renderValue';
import classNames from '../../../../utility/classNames';
import calculateField from '../../utility/calculateFields';
import FormUIOptions from '../../formElements/formUIOptions';
import { isValue } from '../../../../utility/isValue';
import Tooltip from '../../../Tooltip';
import LegacyCalculatedField from './LegacyCalculatedField';
import TwoColumnField from './TwoColumnField';
import TableField from './TableField';

const CalculatedField = memo(function CalculatedField({
  percentageValue,
  annualValue,
  field,
  fieldValue,
  data,
  sectionType,
  dropDowns,
  handleDropDown,
  showTopBorder = true,
  showBar = false,
}) {
  //isConstant -> value does not change based on timescale
  //isAnnual -> original value is annual (not monthly)
  //displayPercentFrom and displayPercentFunction -> display percentage of field, given the values to calculate from and a function
  //displayMonthlyFrom and displayMonthlyFunction -> display monthly amount of field, given the percentage to calculate from and a function
  //dynamic -> field styling is dynamic depending on if the value is positive or negative
  const {
    label,
    name,
    unit,
    description,
    emphasize,
    displayPercentFrom = null,
    displayPercentFunction = null,
    displayMonthlyFrom = null,
    displayMonthlyFunction = null,
    showMonthly = true,
    showAnnual = true,
    absoluteValue = false,
    isAnnual = false,
    isConstant = false,
    groupEnd = false,
    groupStart = false,
    dropDownDependency,
    total,
  } = field;

  if (dropDownDependency && !dropDowns[dropDownDependency]) return null;

  if (field.type === FormUIOptions.Blank)
    return <div className="w-full max-w-sm sm:max-w-full py-[12.3px]"> </div>;

  if (field.type === FormUIOptions.Table)
    return (
      <TableField
        {...{
          percentageValue,
          annualValue,
          field,
          fieldValue,
          data,
          sectionType,
          dropDowns,
          handleDropDown,
        }}
      />
    );

  const isPercentage = unit === '%';

  // if we need to calculate a percentage value for the table
  let generatedPercentage = null;
  if (displayPercentFrom && data) {
    generatedPercentage =
      calculateField(
        {
          ...field,
          derivedFrom: displayPercentFrom,
          calcFunction: displayPercentFunction,
        },
        data
      ) || '-';
  }

  // if we need to calculate a monthly value for the table
  let generatedMonthly = null;
  if (displayMonthlyFrom && data) {
    generatedMonthly = calculateField(
      {
        ...field,
        derivedFrom: displayMonthlyFrom,
        calcFunction: displayMonthlyFunction,
      },
      data
    );
  }

  const getPercentToDisplay = () => {
    if (!percentageValue) return null;
    if (!isPercentage && !generatedPercentage) {
      return <span className="text-sm">-</span>;
    } else if (isPercentage) {
      return renderValue(
        { ...field, decimalPlaces: 2 },
        { [name]: fieldValue }
      );
    } else if (
      generatedPercentage &&
      !Number.isNaN(parseInt(generatedPercentage, 10))
    ) {
      return `${generatedPercentage}%`;
    } else {
      return generatedPercentage || '-';
    }
  };

  const getMonthlyToDisplay = () => {
    if (!showMonthly) return <span className="text-sm">-</span>;
    if (!isValue(generatedMonthly)) {
      return renderValue(field, {
        [name]:
          isConstant || !isAnnual
            ? absoluteValue
              ? Math.abs(fieldValue)
              : fieldValue
            : absoluteValue
            ? isValue(fieldValue)
              ? Math.abs(fieldValue / 12)
              : null
            : isValue(fieldValue)
            ? fieldValue / 12
            : null,
      });
    }
    if (isValue(generatedMonthly)) {
      return renderValue(
        { ...field, type: FormUIOptions.Money },
        {
          [name]: generatedMonthly,
        }
      );
    }
  };

  const getAnnualToDisplay = () => {
    if (!annualValue) return null;
    if (!showAnnual) return <span className="text-sm">-</span>;
    if (!isValue(generatedMonthly)) {
      return renderValue(field, {
        [name]:
          isConstant || isAnnual || fieldValue === null
            ? fieldValue
            : fieldValue * 12,
      });
    }
    if (isValue(generatedMonthly)) {
      return renderValue(
        { ...field, type: FormUIOptions.Money },
        {
          [name]: generatedMonthly * 12,
        }
      );
    }
  };

  const percentToDisplay = getPercentToDisplay();
  const monthlyToDisplay = getMonthlyToDisplay();
  const annualToDisplay = getAnnualToDisplay();

  const fieldLabel = Array.isArray(label)
    ? (fieldValue > 0 && label[0]) || label[1]
    : label;

  const labelPercentage =
    !percentageValue && generatedPercentage ? (
      <span className="text-base">{` (${generatedPercentage}%)`} </span>
    ) : null;

  const labelElement = (
    <div className="text-neutral-600 text-md">
      {fieldLabel} {labelPercentage}
      {description && (
        <Tooltip
          text={description}
          heading={field.tooltipHeading}
          formula={field.tooltipFormula}
          type="info"
        />
      )}
    </div>
  );

  const getCalculatedField = (sectionType, hasBorder) => {
    switch (sectionType) {
      case 'twoColumn':
      case 'chart':
        return (
          <TwoColumnField
            percentageValue={percentageValue}
            annualValue={annualValue}
            percentToDisplay={percentToDisplay}
            monthlyToDisplay={monthlyToDisplay}
            annualToDisplay={annualToDisplay}
            labelElement={labelElement}
            fieldValue={fieldValue}
            handleDropDown={handleDropDown}
            isDropDownOpen={dropDowns && dropDowns[name]}
            field={field}
            hasBorder={hasBorder}
            showTopBorder={showTopBorder}
            showBar={showBar}
          />
        );
      default:
        return (
          <LegacyCalculatedField
            percentageValue={percentageValue}
            annualValue={annualValue}
            percentToDisplay={percentToDisplay}
            monthlyToDisplay={monthlyToDisplay}
            annualToDisplay={annualToDisplay}
            labelElement={labelElement}
            fieldValue={fieldValue}
            field={field}
          />
        );
    }
  };

  return (
    <div
      className={classNames(
        'w-full max-w-sm sm:max-w-full  ',
        emphasize && 'text-neutral-900 font-semibold',
        // groupEnd && 'pb-5',
        groupStart && 'pt-1',
        handleDropDown ||
          (field.subField && 'flex justify-between items-center')
      )}
    >
      {getCalculatedField(sectionType, total)}
    </div>
  );
});

export default CalculatedField;
